import { Flex, IconButton, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FilterSearch } from '../../../../components/FilterBar'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator from '../../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { Tooltip } from '../../../../components/Tooltip'
import { formatDateTime, useAppToast } from '../../../../helpers'
import { BlogPostService } from '../../../../services/BlogPost/BlogPostService'
import { BlogPost } from '../../../../services/BlogPost/types'

/** Só posts ativos e que ainda não estão em nenhuma posição de popular. */
const INITIAL_FILTERS = { search: '', page_size: 10, tag: 'normal', status: 'active' }

interface IProps {
  isOpen: boolean
  onClose: () => void
  /** Chamado depois que a posição foi salva, para a página recarregar os cards. */
  onSaved: () => void
  popularIndex: number
}

export function PopularPostsModal({ isOpen, onClose, onSaved, popularIndex }: IProps) {
  const service = new BlogPostService()
  const [noPopularPosts, setNoPopularPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [reloadKey, setReloadKey] = useState(0)
  const [savingId, setSavingId] = useState<number | null>(null)
  const toast = useAppToast()

  // O reset é feito ao fechar, e não ao abrir: resetar na abertura mudava os filtros logo depois
  // da primeira busca e disparava uma segunda — a lista carregava (e piscava) duas vezes.
  useEffect(() => {
    if (!isOpen) {
      setFilters(INITIAL_FILTERS)
      setPage(1)
    }
  }, [isOpen])

  // Única busca do modal. Fechado ele continua montado, então sem o `isOpen` a página
  // buscava posts só por ter carregado.
  useEffect(() => {
    if (!isOpen) {
      return
    }

    let cancelled = false
    setLoading(true)
    setLoadError(false)
    service
      .list({ ...filters, page })
      .then((response) => {
        if (cancelled) return
        setNoPopularPosts(response.data)
        setTotalPages(Math.ceil(response.total_filtered / filters.page_size))
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(true)
        toast.error('Erro ao carregar os posts', error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, filters, page, reloadKey])

  const setAsPopular = async (id: number) => {
    setSavingId(id)
    service
      .setPopular(popularIndex, id)
      .then(() => {
        toast.success('Post popular salvo com sucesso')
        onSaved()
        onClose()
      })
      .catch((error) => {
        toast.error('Erro ao definir post como popular', error)
      })
      .finally(() => setSavingId(null))
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Escolha um Novo Post Popular para a ${popularIndex}ª Posição`}
      size="editor"
      cancelLabel="Cancelar"
    >
      <Flex justifyContent="flex-end">
        <FilterSearch
          value={filters.search}
          onChange={(search) => {
            setFilters({ ...filters, search })
            setPage(1)
          }}
          placeholder="Buscar por post"
        />
      </Flex>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Título</Th>
              <Th>Criado em</Th>
              <Th>Selecionar</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="posts" onRetry={() => setReloadKey((key) => key + 1)} />
            ) : noPopularPosts.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="post" />
            ) : (
              noPopularPosts.map((post) => (
                <Tr key={post.id}>
                  <Td>{post.id}</Td>
                  <Td maxW="200px">
                    <Tooltip label={post.title}>
                      <Text noOfLines={1}>{post.title}</Text>
                    </Tooltip>
                  </Td>
                  <Td>{formatDateTime(post.created_at)}</Td>
                  <Td>
                    <Tooltip label="Selecionar" placement="right">
                      <IconButton
                        aria-label="Selecionar"
                        colorScheme="green"
                        size="sm"
                        marginLeft={6}
                        isLoading={savingId === post.id}
                        isDisabled={savingId !== null}
                        onClick={() => {
                          setAsPopular(post.id)
                        }}
                      >
                        <Icon name="lni lni-checkmark" />
                      </IconButton>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />
    </FormModal>
  )
}
